import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'SK';

const PRIORITY_COUNTRIES: CountryCode[] = ['SK', 'CZ', 'AT', 'HU', 'PL', 'DE', 'UA', 'GB', 'IE', 'US'];

export interface PhoneCountryOption {
  value: CountryCode;
  label: string;
}

export function getPhoneCountryOptions(locale = 'en'): PhoneCountryOption[] {
  const countryNames = createCountryNames(locale);

  return getCountries()
    .map((country) => ({
      value: country,
      label: `${countryNames.of(country) || country} (+${getCountryCallingCode(country)})`,
    }))
    .sort((a, b) => {
      const priorityA = PRIORITY_COUNTRIES.indexOf(a.value);
      const priorityB = PRIORITY_COUNTRIES.indexOf(b.value);

      if (priorityA !== -1 || priorityB !== -1) {
        if (priorityA === -1) return 1;
        if (priorityB === -1) return -1;
        return priorityA - priorityB;
      }

      return a.label.localeCompare(b.label);
    });
}

export function detectPhoneCountry(value: string | null | undefined, fallbackCountry: CountryCode = DEFAULT_PHONE_COUNTRY): CountryCode {
  const trimmed = value?.trim();
  if (!trimmed) return fallbackCountry;

  const parsed = parsePhoneNumberFromString(trimmed, fallbackCountry);
  return parsed?.country || fallbackCountry;
}

export function normalizePhoneForStorage(value: string | null | undefined, country: CountryCode = DEFAULT_PHONE_COUNTRY): string {
  const trimmed = value?.trim();
  if (!trimmed) return '';

  const parsed = parsePhoneNumberFromString(trimmed, country);
  if (parsed?.number) {
    return parsed.number;
  }

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return trimmed;
  if (trimmed.startsWith('+')) return `+${digits}`;

  const callingCode = getCountryCallingCode(country);
  if (digits.startsWith(callingCode)) return `+${digits}`;

  return `+${callingCode}${digits.replace(/^0+/, '')}`;
}

export function sanitizePhoneInputValue(value: string | null | undefined): string {
  return (value || '').replace(/[^\d\s()+./-]/g, '');
}

export function formatPhoneForDisplay(value: string | null | undefined, country: CountryCode = DEFAULT_PHONE_COUNTRY): string {
  const trimmed = value?.trim();
  if (!trimmed) return '';

  const parsed = parsePhoneNumberFromString(trimmed, country);
  if (parsed?.isValid() || parsed?.isPossible()) {
    return parsed.formatInternational();
  }

  const normalized = normalizePhoneForStorage(trimmed, country);
  if (normalized.startsWith('+')) {
    return formatInternationalFallback(normalized);
  }

  const digits = normalized.replace(/\D/g, '');
  return digits ? groupFromRight(digits) : trimmed;
}

export function formatPhoneForInput(value: string | null | undefined, country: CountryCode = DEFAULT_PHONE_COUNTRY): string {
  const trimmed = sanitizePhoneInputValue(value).trim();
  if (!trimmed) return '';

  const parsed = parsePhoneNumberFromString(trimmed, country);
  if (parsed?.isValid() || parsed?.isPossible()) {
    return groupNationalNumber(parsed.nationalNumber);
  }

  return formatNationalFallback(trimmed, country);
}

function formatInternationalFallback(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return value;

  const country = detectPhoneCountry(`+${digits}`);
  const callingCode = getCountryCallingCode(country);

  if (digits.startsWith(callingCode)) {
    const nationalNumber = digits.slice(callingCode.length);
    return [`+${callingCode}`, groupFromRight(nationalNumber)].filter(Boolean).join(' ');
  }

  return `+${groupFromRight(digits)}`;
}

function formatNationalFallback(value: string, country: CountryCode): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return value;

  const callingCode = getCountryCallingCode(country);
  const nationalNumber = digits.startsWith(callingCode) ? digits.slice(callingCode.length) : digits.replace(/^0+/, '');

  return groupNationalNumber(nationalNumber);
}

function groupNationalNumber(digits: string): string {
  const groups: string[] = [];

  for (let index = 0; index < digits.length; index += 3) {
    groups.push(digits.slice(index, index + 3));
  }

  return groups.join(' ');
}

function groupFromRight(digits: string): string {
  const groups: string[] = [];
  let index = digits.length;

  while (index > 3) {
    groups.unshift(digits.slice(index - 3, index));
    index -= 3;
  }

  if (index > 0) {
    groups.unshift(digits.slice(0, index));
  }

  if (groups.length >= 2 && groups[0].length === 1) {
    groups[0] = groups[0] + groups[1];
    groups.splice(1, 1);
  }

  return groups.join(' ');
}

function createCountryNames(locale: string): { of: (country: CountryCode) => string | undefined } {
  const DisplayNames = (Intl as any)?.DisplayNames;
  if (!DisplayNames) {
    return { of: (country) => country };
  }

  return new DisplayNames([locale], { type: 'region' });
}
